import { createAction } from 'redux-actions';
import moment from 'moment';
import uuid from 'uuid/v4';
import {
  createParams,
  formatParams,
  validateParams,
  getValueFromObjects,
  getCPRMapping,
  getAddressMappingV2,
  getCustomerAge,
} from './common';
import Product from './product';
import * as types from '../../constants/Api';
import { createErrorBody } from '../error';
import { doVechicleModelMock, isVechicleModelMock } from '../mock/carVehicleMock';

class CarServiceV2 extends Product {
  static TYPE = 'car';
  static VEHICLE_DETAILS_BY_REGISTRATION = 'car/v2/vehicle';
  static VEHICLE_MODEL = 'car/v2/vehiclemodel';
  static GET_PRICE_V2 = 'car/v2/price';
  static UPDATE_PRICE_V2 = 'car/v2/price';
  static CAR_PURCHASE = 'car/v2/purchasepolicy';
  static CAR_TJM_PAYMENT = 'tjmproduct/v1/payment';

  static validateCarRegistrationNumber = createAction(
    types.CAR_REGISTRATION_NUMBER_VALID
  );
  static validatedCarRegistrationNumber = createAction(
    types.CAR_REGISTRATION_NUMBER_VALIDATED
  );
  static createdCar = createAction(types.CAR_CREATED);
  static createCar = createAction(types.CAR_CREATE);
  static updatedCar = createAction(types.CAR_UPDATED);
  static updateCar = createAction(types.CAR_UPDATE);
  static preparedCar = createAction(types.CAR_PREPARED);
  static prepareCar = createAction(types.CAR_PREPARE);
  static updateRecordedMileage = createAction(types.CAR_UPDATE_MILEAGE);
  static createPayment = createAction(types.PAYMENT_CREATE);
  static createdPayment = createAction(types.PAYMENT_CREATED);

  constructor() {
    super(
      CarServiceV2.TYPE,
      CarServiceV2.createCar,
      CarServiceV2.updateCar,
      CarServiceV2.prepareCar
    );
    super.prepareCreateUpdateResponse = this.prepareCreateUpdateResponse;
  }

  createPaymentAction(payload) {
    return dispatch => dispatch(CarServiceV2.createPayment(payload));
  }

  createdPaymentAction(payload) {
    return dispatch => dispatch(CarServiceV2.createdPayment(payload));
  }

  validateCarRegistrationNumberAction(params, formValues) {
    return (dispatch) =>
      dispatch(
        this.requestVehicleModelsByRegistrationNumber(params, formValues)
      )
        .then((data) => {
          dispatch(this.validatedCarRegistrationNumberAction(data));
          if (typeof formValues.setValues === 'function') {
            // We use setValues to update the form values back in react.
            formValues.setValues({ ...formValues, ...data });
          }
        })
        .catch(() => {
          // Save state for service error.
          dispatch(
            this.validatedCarRegistrationNumberAction({
              registrationNumberValid: null,
              registrationNumberNoService: true,
            })
          );

          if (typeof formValues.setValues === 'function') {
            // We use setValues to update the form values back in react.
            formValues.setValues({
              ...formValues,
              registrationNumberValid: null,
              registrationNumberNoService: true,
            });
          }
        });
  }

  validatedCarRegistrationNumberAction(payload) {
    return (dispatch) =>
      dispatch(CarServiceV2.validatedCarRegistrationNumber(payload));
  }

  createdCarAction(payload) {
    return (dispatch) => dispatch(CarServiceV2.createdCar(payload));
  }

  createCarActionV2(mappedParams, actionOnErrors, reqModel, formValues, serviceName) {
    return (dispatch, getState) => {
      return dispatch(
        this.requestCreateProductV2(
          serviceName,
          mappedParams,
          reqModel,
          formValues
        )
      )
        .then((data) => {
          dispatch(this.createdCarAction(data));
        })
        .catch((data) => {
          return this.handleError(actionOnErrors, data, getState, dispatch);
        });
    };
  }

  updatedCarAction(payload) {
    return (dispatch) => dispatch(CarServiceV2.updatedCar(payload));
  }

  updateCarActionV2(mappedParams, actionOnErrors, reqModel, formValues, serviceName) {
    return (dispatch, getState) => {
      return dispatch(
        this.requestCreateProductV2(
          serviceName,
          mappedParams,
          reqModel,
          formValues
        )
      )
        .then((data) => {
          dispatch(this.updatedCarAction(data));
        })
        .catch((data) => {
          return this.handleError(actionOnErrors, data, getState, dispatch);
        });
    };
  }

  updateRecordedMileageAction(payload) {
    return (dispatch) => dispatch(CarServiceV2.updateRecordedMileage(payload));
  }

  createUpdateCarAction(params, actionOnErrors, values) {
    return (dispatch, getState) => {
      const state = getState();
      const formValues = { ...values, ...state.userData };
      const mappedParams = params
        ? createParams(params, formValues, state)
        : {};
      const errors = params ? validateParams(mappedParams, params) : [];
      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }

      const requestModel = {
        trygDrive:
          getValueFromObjects('trygDrive', mappedParams, formValues) ||
          state.userData.subProduct.includes('trygdrive'),
        car: {
          registrationnumber: (
            getValueFromObjects(
              'registrationNumber',
              mappedParams,
              formValues
            ) || ''
          ).toUpperCase(),
          primaryUseName: getValueFromObjects(
            'primaryUseName',
            mappedParams,
            formValues
          ) || '',
          vehicleMakeId: getValueFromObjects(
            'vehicleMakeId',
            mappedParams,
            formValues
          ),
          vehicleIdentificationNumber: getValueFromObjects(
            'vehicleIdentificationNumber',
            mappedParams,
            formValues
          ),
          firstTimePaymentSum: getValueFromObjects(
            'firstTimePaymentSum',
            mappedParams,
            formValues
          ) || '',
          drivePowerTypeName: getValueFromObjects(
            'drivePowerTypeName',
            mappedParams,
            formValues
          ) || '',
          tireAndWheelRimSum: getValueFromObjects(
            'tireAndWheelRimSum',
            mappedParams,
            formValues
          ) || '',
          mileage: getValueFromObjects('mileage-car', mappedParams, formValues),
          carLeased:
            getValueFromObjects('carLeased', mappedParams, formValues) ||
            false,
          leasingCompanyId: getValueFromObjects('leasingCompanyId', mappedParams, formValues) || '',
          standstill:
            getValueFromObjects('standStill', mappedParams, formValues) ||
            false,
          recordedMileage:
            getValueFromObjects(
              'recordedMileage-car',
              mappedParams,
              formValues
            ) || '',
          recordedMileageDate:
            getValueFromObjects(
              'recordedMileageDate',
              mappedParams,
              formValues
            ) || null,
        },
        driver: {
          age: getCustomerAge(mappedParams, formValues),
          birthDate: getValueFromObjects('birthdate', mappedParams, formValues) || '',
          civilRegistrationNumber: getCPRMapping(mappedParams, formValues),
          rolePlayerId: getValueFromObjects('rolePlayerId', mappedParams, formValues) || '',
          claimCount: getValueFromObjects(
            'claimCount-car',
            mappedParams,
            formValues
          ),
          postalAddress: getAddressMappingV2(mappedParams, formValues),
          driverExperience: getValueFromObjects(
            'driverExperience-car',
            mappedParams,
            formValues
          ),
          isInRKI:
          getValueFromObjects(
            'isInRKI',
            mappedParams,
            formValues
          ) || false,
          KVHX: '',
        },
        deductible: getValueFromObjects(
          'deductible-car',
          mappedParams,
          formValues
        ),
      };

      if (state.userData.car) {
        return dispatch(
          this.updateCarActionV2(
            mappedParams,
            actionOnErrors,
            requestModel,
            {
              ...formValues,
            },
            CarServiceV2.UPDATE_PRICE_V2)
        );
      }

      const AGE_MILEAGE_YEAR = getValueFromObjects(
        'recordedMileage-car',
        mappedParams,
        formValues
      );
      const modelYear = getValueFromObjects(
        'modelYear',
        mappedParams,
        formValues
      );
      let recordedMileage =
        Number(AGE_MILEAGE_YEAR) * (new Date().getFullYear() - modelYear);
      recordedMileage = recordedMileage > 250000 ? 250000 : recordedMileage;

      dispatch(
        this.updateRecordedMileageAction({
          'recordedMileage-car': `${recordedMileage}`,
          recordedMileageDate: moment().format('YYYY-MM-DD'),
        })
      );

      formValues.recordedMileage = `${recordedMileage}`;
      return dispatch(
        this.createCarActionV2(mappedParams,
          actionOnErrors,
          requestModel,
          formValues,
          CarServiceV2.GET_PRICE_V2)
      );
    };
  }

  requestVehicleModelsByRegistrationNumber(params, formValues) {
    return (dispatch, getState) => {
      if (isVechicleModelMock(formValues)) {
        return doVechicleModelMock(formValues);
      }

      if (this.shouldUpdate(getState())) {
        dispatch(CarServiceV2.validateCarRegistrationNumber());
        let mappedParams = params
          ? createParams(params, formValues, getState())
          : {};
        const errors = params ? validateParams(mappedParams, params) : [];

        if (errors.length > 0) {
          return Promise.reject(createErrorBody('Missing parameters', errors));
        }

        mappedParams = {
          general: {
            globalTransactionId: uuid(),
          },
          registrationNumber: (
            getValueFromObjects(
              'registrationNumber',
              mappedParams,
              formValues
            ) || ''
          ),
        };

        return this.callApiPost(
          CarServiceV2.VEHICLE_DETAILS_BY_REGISTRATION,
          mappedParams,
          dispatch
        ).then((response) => {
          // Let the calling code know there's nothing to wait for.
          const modelList = response.vehicleModelList;
          const modelVariant = Object.keys(modelList).reduce((result, key) => {
            if (modelList[key].vehicleMakeId) {
              return [
                ...result,
                {
                  key: modelList[key].vehicleMakeId,
                  value: modelList[key].variantName,
                  fuelType: modelList[key].fuelType,
                },
              ];
            }
            return result;
          }, []);
          const hasData = modelVariant.length > 0;
          const model = modelList[0];
          const defaultModel = {
              modelYear: model.modelYear,
              make: model.make,
              model: model.model,
              gpsRequired: (model.GPSRequired === 'true'),
              vehicleIdentificationNumber: response.vehicleIdentification.vehicleIdentificationNumber,
              vehicleMakeIdRegNo: model.vehicleMakeId,
              drivePowerTypeName: response.vehicleIdentification.drivePowerTypeName,
            };
          if (modelVariant.length === 1){
            defaultModel.variantName = model.variantName;
            defaultModel.vehicleMakeId = model.vehicleMakeId;
            defaultModel.fuelType = model.fuelType;
          }
          
          if (hasData) {
            return {
              ...defaultModel,
              registrationNumberValid: hasData,
              registrationNumberNoService: false,
              registrationNumberProvided: hasData,
              modelVariant,
              showModelVariant: modelVariant.length > 1,
              registrationNumberInput: getValueFromObjects('registrationNumber', mappedParams, formValues),
            };
          }
          return {
            registrationNumberValid: hasData,
            registrationNumberNoService: false,
            vehicleMakeIdRegNo: '',
            registrationNumberProvided: hasData,
          };
        });
      }

      // Let the calling code know there's nothing to wait for.
      return Promise.resolve();
    };
  }

  getVehicleModels(params, dispatch) {
    const mappedParams = formatParams(params, []);
    const vehicleModels = [];
    return this.getAllVehicleModels(mappedParams, vehicleModels, dispatch);
  }
  // Loading all models for the brand and year
  getAllVehicleModels(mappedParams, vehicleModels, dispatch) {
    return this.callApiPost(
      CarServiceV2.VEHICLE_MODEL,
      mappedParams,
      dispatch
    ).then((response) => {
      const models = response.vehicleModelList.reduce((result, key) => {
        return [
          ...result,
          {
            gpsRequired: Boolean(key.GPSRequired),
            model: key.model,
            vehicleMakeId: key.vehicleMakeId,
          },
        ];
      }, []);
      let modelList = [...vehicleModels,
        ...models];
      if (response.moreRecords === 'true') {
        const newParams = mappedParams;
        newParams.nextPageStart = response.nextStartCode;
        modelList = this.getAllVehicleModels(newParams, modelList, dispatch);
      }
      return modelList;
    });
  }

  getExcessAmount(formValues, dispatch) {
    return super.getExcessAmount(
      formValues,
      this.getCarMapping(formValues),
      dispatch
    );
  }

  activationDateGetAction(formValues) {
    return super.activationDateGetAction(
      formValues,
      this.getCarMapping(formValues)
    );
  }
  activationDateGetActionService(formValues, baseModel, params) {
    return super.activationDateGetAction(
      formValues,
      baseModel,
      params
    );
  }

  getActivationDates(formValues) {
    return super.getActivationDates(formValues, this.getCarMapping(formValues));
  }

  getActivationDatesService(formValues, baseModel, dispatch) {
    return super.getActivationDates(formValues, baseModel, dispatch);
  }

  getActivationDatesMonthYear(formValues) {
    return super.getActivationDatesMonthYear(formValues, this.getCarMapping(formValues));
  }

  getActivationDatesMonthYearService(formValues, baseModel) {
    return super.getActivationDatesMonthYear(formValues, baseModel);
  }

  getCarMapping(formValues, mappedParams = {}) {
    return {
      product: {
        type: CarServiceV2.TYPE,
        registrationNumber: getValueFromObjects(
          'registrationNumber',
          mappedParams,
          formValues
        ),
        equipmentValue: getValueFromObjects(
          'equipmentValue-car',
          mappedParams,
          formValues
        ),
        vehicleMakeId: getValueFromObjects(
          'vehicleMakeId',
          mappedParams,
          formValues
        ),
        mileage: getValueFromObjects('mileage-car', mappedParams, formValues),
        leasedCar:
          getValueFromObjects('leasedCar', mappedParams, formValues) || 'false',
        modelYear: getValueFromObjects('modelYear', mappedParams, formValues),
        make: getValueFromObjects('make', mappedParams, formValues),
        model: getValueFromObjects('model', mappedParams, formValues),
      },
    };
  }

  purchase(params, values, actionOnErrors) {
    return (dispatch, getState) => {
      const state = getState();
      const formValues = { ...values, ...state.userData };
      const mappedParams = params
        ? createParams(params, formValues, state)
        : {};
      const errors = params ? validateParams(mappedParams, params) : [];

      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }

      const requestModel = {
        car: {
          recordedMileage: `${
            getValueFromObjects(
              'recordedMileage-car',
              mappedParams,
              formValues
            ) || ''
          }`,
          recordedMileageDate: getValueFromObjects(
            'recordedMileageDate',
            mappedParams,
            formValues
          ),
          registrationId: getValueFromObjects(
            'registrationNumber',
            mappedParams,
            formValues
          ).toUpperCase(),
          startPerVehicleRegistration: !getValueFromObjects(
            'terminateExistingInsurance',
            mappedParams,
            formValues
          ),
          vehicleIdentificationNumber: getValueFromObjects(
            'vehicleIdentificationNumber',
            mappedParams,
            formValues
          ),
        },
        electronicContact: {
          smsPermissionForPromotion: getValueFromObjects('perm_contactInfo_accept', mappedParams, formValues) || false,
        },
        otherDriverExpirence: {
          otherDrivingExperienceProvided: false,
          otherDrivingExperience: '',
        },
      };

      return dispatch(super.purchaseV2(CarServiceV2.CAR_PURCHASE, mappedParams,
        requestModel, formValues, actionOnErrors));
    };
  }

  getFeeAmountPerPayment(feeList, feeCode) {
    const feeAmountPerPaymentList =
      feeList &&
      feeList.filter(
        (fee) =>
          Object.prototype.hasOwnProperty.call(fee, 'feeCode') &&
          fee.feeCode &&
          fee.feeCode === feeCode
      );
    return (feeAmountPerPaymentList && feeAmountPerPaymentList[0] &&
      Object.prototype.hasOwnProperty.call(
        feeAmountPerPaymentList[0],
        'feeAmountPerPayment'
      )
      ? feeAmountPerPaymentList[0].feeAmountPerPayment
      : 0);
  }

  calculatePriceAndFee(price, feeList, formValues) {
    const guaranteeFee =
      Math.round(this.getFeeAmountPerPayment(feeList, 'GARB') * 100) / 100;
    const priceWithFee = Math.round(price.premiumAmount * 100) / 100;

    return {
      premiumPrice: price.premiumWithOutFee,
      guaranteeFee: `${guaranteeFee}`,
      priceWithFee: `${priceWithFee}`,
      insuranceFee: price.insuranceToll,
      paymentFee: formValues.paymentFee || '',
    };
  }

  prepareCreateUpdateResponse(payload, mappedParams, formValues) {
    const response = {
      sessionKey: payload.generalInsurance.sessionKey,
      equipmentValue:
        payload.car.equipmentValue === '0'
          ? payload.usedParams.car.equipmentValue.replace(/\./g, '')
          : payload.car.equipmentValue,
      mileage: payload.car.mileage,
      leasedCar: payload.car.leasedCar,
      deductible: payload.deductible,
      recordedMileage: formValues.recordedMileage,
      recordedMileageDate: payload.car.recordedMileageDate,
      paymentFrequency: payload.payment.paymentFrequency,
      paymentMethod: payload.payment.paymentMethod,
      driverExperience:
        payload.driver.driverExperience === '99'
          ? '5'
          : payload.driver.driverExperience,
      claimCount: payload.driver.claimCount,
      KVHX: payload.driver.postalAddress.KVHX,
      priceApiResponse: [
        {
          offeredPrice: this.calculatePriceAndFee(
            payload.price.offeredPrice,
            payload.price.fees,
            formValues
          ),
          listPrice: this.calculatePriceAndFee(
            payload.price.listPrice,
            payload.price.fees,
            formValues
          ),
          advantagePrice: this.calculatePriceAndFee(
            payload.price.advantagePrice,
            payload.price.fees,
            formValues
          ),
          discountAmount:
            this.calculatePriceAndFee(
              payload.price.listPrice,
              payload.price.fees,
              formValues
            ).priceWithFee -
            this.calculatePriceAndFee(
              payload.price.offeredPrice,
              payload.price.fees,
              formValues
            ).priceWithFee,
        },
      ],
      totalOfferedPrice: {
        priceWithFee: payload.price.offeredPrice.premiumWithOutFee,
      },
      usedParams: payload.usedParams,
    };

    const listPrice = response.priceApiResponse[0].listPrice.premiumPrice;
    const offeredPrice = response.priceApiResponse[0].offeredPrice.premiumPrice;
    const discount = Math.round(((listPrice - offeredPrice) / listPrice) * 100);

    response.discount = `${discount < 20 ? 20 : discount}`;

    return response;
  }
}

export const carServiceV2 = new CarServiceV2();